import { useEffect, useMemo, useRef, useState } from "react";

/* Everything the panel can do, on one keystroke.

   The controls this reaches are spread over a header, a right-rail card with
   three tabs and a settings drawer — which is the right place for them when you
   are looking for them, and the wrong one when you are mid-conversation with
   chat and need the kill switch now. So the layout keeps its shape and this
   gives it a second, faster door. */

export interface Command {
  id: string;
  label: string;
  /* Shown greyed after the label: what it will do, or which key it is bound to. */
  hint?: string;
  danger?: boolean;
  run: () => void;
}

interface Props {
  open: boolean;
  commands: Command[];
  onClose: () => void;
}

export function CommandPalette({ open, commands, onClose }: Props) {
  const [needle, setNeedle] = useState("");
  const [cursor, setCursor] = useState(0);
  const field = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);

  const matches = useMemo(() => {
    const query = needle.toLowerCase().trim();
    if (!query) return commands;
    return commands.filter((command) =>
      `${command.label} ${command.hint ?? ""}`.toLowerCase().includes(query),
    );
  }, [commands, needle]);

  // The list shrinks under the highlight as you type.
  const at = Math.min(cursor, Math.max(0, matches.length - 1));

  useEffect(() => {
    if (!open) return;
    setNeedle("");
    setCursor(0);
    field.current?.focus();
  }, [open]);

  useEffect(() => {
    list.current
      ?.querySelector<HTMLElement>(".command.cursor")
      ?.scrollIntoView({ block: "nearest" });
  }, [at]);

  if (!open) return null;

  function choose(command: Command | undefined) {
    if (!command) return;
    onClose();
    command.run();
  }

  return (
    <div
      className="dialog-scrim palette-scrim"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="palette" role="dialog" aria-modal="true" aria-label="Commands">
        <input
          ref={field}
          type="text"
          className="palette-input"
          placeholder="Type a command…"
          autoComplete="off"
          spellCheck={false}
          value={needle}
          onChange={(event) => {
            setNeedle(event.target.value);
            setCursor(0);
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              onClose();
              return;
            }
            if (event.key === "Enter") {
              event.preventDefault();
              choose(matches[at]);
              return;
            }
            if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
            event.preventDefault();
            if (!matches.length) return;
            const step = event.key === "ArrowDown" ? 1 : -1;
            setCursor((was) => (was + step + matches.length) % matches.length);
          }}
        />

        <div className="palette-list" ref={list} role="listbox" aria-label="Commands">
          {matches.length === 0 ? (
            <div className="muted palette-empty">Nothing matches.</div>
          ) : (
            matches.map((command, index) => (
              <button
                key={command.id}
                role="option"
                aria-selected={index === at}
                className={
                  "command" +
                  (index === at ? " cursor" : "") +
                  (command.danger ? " danger" : "")
                }
                // mousedown would blur the input and close before the click
                onMouseDown={(event) => event.preventDefault()}
                onMouseEnter={() => setCursor(index)}
                onClick={() => choose(command)}
              >
                <span className="command-label">{command.label}</span>
                {command.hint && <span className="command-hint">{command.hint}</span>}
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
